// src/services/apaleo/accounts.ts
// Extracted verbatim from src/services/apaleo.ts (Phase 5 of
// plans/monolith-migration-plan.md). `this` is typed via ThisType<ApaleoClient>.

import type { AccountEntry, AccountsSchemaEntry } from './types';
import type { ApaleoClient } from './client-contract';
import { IS_DEV, PAYMENT_API_URL } from './config';

const part: ThisType<ApaleoClient> = {
async fetchAccountsSchema(propertyId: string): Promise<AccountEntry[]> {
  if (IS_DEV) return []; // Mock for dev
  try {
    const data = (await this._fetch(`/finance/v1/accounts/schema?propertyId=${propertyId}&depth=4&languageCode=de`)) as
      | {
          globalAccounts?: AccountsSchemaEntry[];
          guestAccounts?: AccountsSchemaEntry[];
          externalAccounts?: AccountsSchemaEntry[];
          bookingAccounts?: AccountsSchemaEntry[];
        }
      | null;
    if (!data) return [];

    const allAccounts: AccountEntry[] = [];
    const extractAccounts = (accounts: AccountsSchemaEntry[] | undefined) => {
      if (!accounts) return;
      accounts.forEach(acc => {
        allAccounts.push({
          number: acc.accountNumber,
          name: acc.name,
          type: acc.type,
          parentNumber: acc.parentNumber,
          isArchived: acc.isArchived
        });
        if (acc.subAccounts) extractAccounts(acc.subAccounts);
      });
    };

    extractAccounts(data.globalAccounts);
    extractAccounts(data.guestAccounts);
    extractAccounts(data.externalAccounts);
    extractAccounts(data.bookingAccounts);

    // Deduplicate by account number
    const uniqueAccounts: AccountEntry[] = [];
    const seenNumbers = new Set<string>();
    for (const a of allAccounts) {
      if (!a.isArchived && !seenNumbers.has(a.number || "")) {
        uniqueAccounts.push(a);
        seenNumbers.add(a.number || "");
      }
    }

    return uniqueAccounts;
  } catch (error) {
    console.error(`Fehler beim Laden der Accounts Schema für Property ${propertyId}:`, error);
    return [];
  }
},

async fetchTransactionsExport(
  propertyId: string,
  from: string,
  to: string,
): Promise<unknown[]> {
  if (IS_DEV) return []; // Mock for dev
  try {
    // Apaleo endpoints filter by timestamp, not Accounting Date (t.date).
    // To ensure we get all transactions for the requested Accounting Dates (e.g. Night Audit running at 2 AM the next day),
    // we expand the query window by +/- 2 days, and then strictly filter locally by t.date.
    const fromDate = new Date(from);
    fromDate.setDate(fromDate.getDate() - 2);
    const toDate = new Date(to);
    toDate.setDate(toDate.getDate() + 2);

    const fromDateTime = `${this._localDateIso(fromDate)}T00:00:00Z`;
    const toDateTime = `${this._localDateIso(toDate)}T23:59:59Z`;

    const url = `/finance/v1/accounts/export?propertyId=${propertyId}&from=${fromDateTime}&to=${toDateTime}`;
    const data = (await this._fetch(url, { method: "POST" })) as
      | { transactions?: { date?: string }[] }
      | null;
    const allTransactions = data?.transactions || [];

    // Filter strictly by the requested Accounting Date
    return allTransactions.filter(t => {
      if (!t.date) return false;
      return t.date >= from && t.date <= to;
    });
  } catch (error) {
    console.error(`Fehler beim Laden der Transactions Export für Property ${propertyId}:`, error);
    throw error;
  }
},

async fetchSettlements(
  propertyId: string | null = null,
  merchantAccountCode: string | null = null,
  limit: number | null = null,
): Promise<unknown[]> {
  try {
    let url = `${PAYMENT_API_URL}/payment/v1/reports/settlements`;
    const params: string[] = [];
    if (propertyId && propertyId !== "All") {
      params.push(`propertyIds=${propertyId}`);
    }
    if (merchantAccountCode) {
      params.push(`merchantAccountCodes=${merchantAccountCode}`);
    }
    if (limit) {
      params.push(`pageSize=${limit}`);
    }
    if (params.length > 0) {
      url += `?${params.join("&")}`;
    }
    const data = (await this._fetch(url)) as
      | { settlements?: unknown[]; items?: unknown[] }
      | null;
    return data?.settlements || data?.items || [];
  } catch (error) {
    console.error("Fehler beim Laden der Settlements:", error);
    throw error;
  }
},

async fetchPropertyPaymentConfig(propertyId: string): Promise<unknown> {
  try {
    return await this._fetch(`${PAYMENT_API_URL}/payment/v1/configuration/properties/${propertyId}`);
  } catch (error) {
    console.error(`Fehler beim Laden der Payment-Konfiguration für ${propertyId}:`, error);
    throw error;
  }
},

async fetchSettlementCsv(id: string): Promise<string | null> {
  try {
    const res = (await this._fetch(`${PAYMENT_API_URL}/payment/v1/reports/settlements/${id}/csv`, {
      headers: { Accept: "text/csv" },
      raw: true
    })) as Response | null;
    if (!res || !res.ok) return null;
    return await res.text();
  } catch (error) {
    console.error(`Fehler beim Laden des Settlement-CSV für ${id}:`, error);
    throw error;
  }
},

async fetchSettlementXlsx(id: string): Promise<Blob | null> {
  try {
    const res = (await this._fetch(`${PAYMENT_API_URL}/payment/v1/reports/settlements/${id}/xlsx`, {
      headers: { Accept: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" },
      raw: true
    })) as Response | null;
    if (!res || !res.ok) return null;
    return await res.blob();
  } catch (error) {
    console.error(`Fehler beim Laden des Settlement-XLSX für ${id}:`, error);
    throw error;
  }
},

};

export default part;
